import { useEffect, type RefObject } from 'react';

/**
 * Dismissal for any popover (M4.5): pointer down anywhere outside the element, Escape, and a
 * scroll anywhere in the document (the message list, a sheet). The trigger toggles; the caller
 * closes after choosing an item. Nothing is attached while closed.
 */
export function useDismiss(
  open: boolean,
  ref: RefObject<HTMLElement | null>,
  onClose: () => void,
  opts: { ignore?: RefObject<HTMLElement | null> | undefined } = {},
): void {
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (ref.current?.contains(target)) return;
      if (opts.ignore?.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    const onScroll = (e: Event) => {
      // A scroll inside the popover itself (a long menu) keeps it open.
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open, ref, onClose, opts.ignore]);
}
